import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import * as THREE from "three";
import "./webgl-reveal.scss";

const WebGLReveal = ({
    primaryImg,
    secondaryImg,
    displacementIntensity = 0.35,
    speed = 0.1,
}) => {
    const containerRef = useRef();
    const sceneRef = useRef({
        scene: null,
        camera: null,
        renderer: null,
        material: null,
        animationFrame: null,
        transition: 0,
        targetTransition: 0,
    });

    useEffect(() => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const width = container.clientWidth;
        const height = container.clientHeight;
        const currentRef = sceneRef.current;

        // Cleanup previous instance if any
        const cleanup = () => {
            if (currentRef.animationFrame) {
                cancelAnimationFrame(currentRef.animationFrame);
                currentRef.animationFrame = null;
            }
            if (currentRef.renderer) {
                currentRef.renderer.dispose();
                if (container.contains(currentRef.renderer.domElement)) {
                    container.removeChild(currentRef.renderer.domElement);
                }
                currentRef.renderer = null;
            }
            if (currentRef.material) {
                if (currentRef.material.uniforms.uTexture1.value)
                    currentRef.material.uniforms.uTexture1.value.dispose();
                if (currentRef.material.uniforms.uTexture2.value)
                    currentRef.material.uniforms.uTexture2.value.dispose();
                currentRef.material.dispose();
                currentRef.material = null;
            }
            if (currentRef.scene) {
                currentRef.scene.traverse((object) => {
                    if (object.geometry) object.geometry.dispose();
                    if (object.material) {
                        if (Array.isArray(object.material)) {
                            object.material.forEach((mat) => mat.dispose());
                        } else {
                            object.material.dispose();
                        }
                    }
                });
                currentRef.scene = null;
            }
        };

        cleanup();

        // Scene init
        const scene = new THREE.Scene();
        const camera = new THREE.OrthographicCamera(
            width / -2,
            width / 2,
            height / 2,
            height / -2,
            1,
            1000
        );
        camera.position.z = 1;

        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Limit pixel ratio for performance
        renderer.setSize(width, height);
        container.appendChild(renderer.domElement);

        // Load Textures
        const loader = new THREE.TextureLoader();
        const texture1 = loader.load(primaryImg);
        const texture2 = loader.load(secondaryImg);

        // Custom Shaders
        const vertexShader = `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `;

        const fragmentShader = `
            varying vec2 vUv;
            uniform sampler2D uTexture1;
            uniform sampler2D uTexture2;
            uniform float uTransition;
            uniform float uIntensity;
            uniform float uTime;
            uniform vec2 uMouse;
            uniform float uAspect;

            void main() {
                vec2 correctedMouse = uMouse;
                vec2 correctedUv = vUv;
                correctedUv.y /= uAspect;
                correctedMouse.y /= uAspect;

                float dist = distance(correctedUv, correctedMouse);
                
                float radius = 0.08 + uTransition * 0.03;
                float mask = 1.0 - smoothstep(radius - 0.02, radius + 0.02, dist);
                
                vec4 _texture1 = texture2D(uTexture1, vUv);
                vec4 _texture2 = texture2D(uTexture2, vUv);

                gl_FragColor = mix(_texture1, _texture2, mask);
            }
        `;

        const geometry = new THREE.PlaneGeometry(width, height);
        const material = new THREE.ShaderMaterial({
            uniforms: {
                uTexture1: { value: texture1 },
                uTexture2: { value: texture2 },
                uTransition: { value: 0 },
                uIntensity: { value: displacementIntensity },
                uTime: { value: 0 },
                uMouse: { value: new THREE.Vector2(0.5, 0.5) },
                uAspect: { value: width / height },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
        });

        const mesh = new THREE.Mesh(geometry, material);
        scene.add(mesh);

        currentRef.scene = scene;
        currentRef.camera = camera;
        currentRef.material = material;
        currentRef.renderer = renderer;

        const animate = (time) => {
            if (!currentRef.renderer) return;

            currentRef.transition +=
                (currentRef.targetTransition - currentRef.transition) * speed;
            material.uniforms.uTransition.value = currentRef.transition;
            material.uniforms.uTime.value = time * 0.001;

            if (window.innerWidth < 768) {
                const t = time * 0.001;
                const x = 0.5 + Math.cos(t * 0.5) * 0.25;
                const y = 0.6 + Math.sin(t * 0.7) * 0.2;
                material.uniforms.uMouse.value.set(x, y);
                material.uniforms.uTransition.value = 1.0;
            }

            renderer.render(scene, camera);
            currentRef.animationFrame = requestAnimationFrame(animate);
        };
        currentRef.animationFrame = requestAnimationFrame(animate);

        const handleResize = () => {
            if (!container || !renderer) return;
            const w = container.clientWidth;
            const h = container.clientHeight;
            renderer.setSize(w, h);
            camera.left = w / -2;
            camera.right = w / 2;
            camera.top = h / 2;
            camera.bottom = h / -2;
            camera.updateProjectionMatrix();
            mesh.geometry.dispose();
            mesh.geometry = new THREE.PlaneGeometry(w, h);
            material.uniforms.uAspect.value = w / h;
        };

        const handleMouseMove = (e) => {
            if (!material) return;
            const rect = container.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width;
            const y = 1.0 - (e.clientY - rect.top) / rect.height;
            material.uniforms.uMouse.value.set(x, y);
        };

        window.addEventListener("resize", handleResize);
        container.addEventListener("mousemove", handleMouseMove);

        return () => {
            window.removeEventListener("resize", handleResize);
            container.removeEventListener("mousemove", handleMouseMove);
            cleanup();
        };
    }, [primaryImg, secondaryImg, displacementIntensity, speed]);

    const handleMouseEnter = () => {
        sceneRef.current.targetTransition = 1;
    };

    const handleMouseLeave = () => {
        sceneRef.current.targetTransition = 0;
    };

    return (
        <div
            ref={containerRef}
            className="webgl-reveal"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            role="presentation"
        />
    );
};

WebGLReveal.propTypes = {
    primaryImg: PropTypes.string.isRequired,
    secondaryImg: PropTypes.string.isRequired,
    displacementIntensity: PropTypes.number,
    speed: PropTypes.number,
};

export default WebGLReveal;
